import { useEffect, useState } from "react";

/**
 * Subscribe to a CSS media query. SSR/test-safe: returns `false` when
 * `window.matchMedia` is unavailable (e.g. jsdom without a mock), so callers
 * default to the desktop/TV layout.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    setMatches(mql.matches); // re-sync in case the query changed
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

export interface CompactViewport {
  /** Phone or tablet (≤1024px): scroll the content instead of paginating it. */
  isCompact: boolean;
  /** Phone (≤640px): stack each row as a card instead of a column grid. */
  isPhone: boolean;
}

/** Breakpoints for the planner full-screen views (and reusable elsewhere). */
export const COMPACT_VIEWPORT_QUERY = "(max-width: 1024px)";
export const PHONE_VIEWPORT_QUERY = "(max-width: 640px)";

/**
 * Viewport tier for layouts that behave differently on a TV/desktop vs a
 * handheld: `isCompact` (phone or tablet) swaps a paged/kiosk model for normal
 * scrolling; `isPhone` further collapses column grids into stacked cards.
 */
export function useCompactViewport(): CompactViewport {
  const isCompact = useMediaQuery(COMPACT_VIEWPORT_QUERY);
  const isPhone = useMediaQuery(PHONE_VIEWPORT_QUERY);
  return { isCompact, isPhone };
}
